"use client"

import type { HttpTypes } from "@medusajs/types"
import posthog from "posthog-js"

const SESSION_STORAGE_KEY = "posthog_session_id"
const SOURCE_CHANNEL_STORAGE_KEY = "posthog_source_channel"
const IDENTIFIED_USER_STORAGE_KEY = "posthog_identified_user_id"

/**
 * Internal/test accounts whose activity must never reach PostHog (they would
 * otherwise pollute revenue, funnels and conversion rates). Mirrors the
 * backend analytics exclusion rules. Keep in sync.
 */
const INTERNAL_EMAIL_PATTERNS = [
  "@krek.si",
  "niklas",
  "niklas.krek@gmail.com",
  "example@",
  "@example.com",
]
const INTERNAL_NAME_PATTERNS = ["niklas"]

export function isInternalEmail(email?: string | null): boolean {
  if (!email) {
    return false
  }
  const normalized = email.toLowerCase().trim()
  return INTERNAL_EMAIL_PATTERNS.some((pattern) => normalized.includes(pattern))
}

export function isInternalName(...names: (string | null | undefined)[]): boolean {
  return names.some((name) => {
    if (!name) {
      return false
    }
    const normalized = name.toLowerCase().trim()
    return INTERNAL_NAME_PATTERNS.some((pattern) => normalized.includes(pattern))
  })
}

export function isInternalUser(params: {
  email?: string | null
  firstName?: string | null
  lastName?: string | null
}): boolean {
  return (
    isInternalEmail(params.email) ||
    isInternalName(params.firstName, params.lastName)
  )
}

/**
 * Opt the current browser out of PostHog capture when the user is an
 * internal/test account. Opt-out persists locally, so once an internal user is
 * seen (login or checkout) their subsequent events are suppressed too. Returns
 * true if an opt-out was applied.
 */
export function applyInternalEmailOptOut(
  email?: string | null,
  names?: { firstName?: string | null; lastName?: string | null }
): boolean {
  if (canUseBrowserApis() && posthog.__loaded && isInternalUser({ email, ...names })) {
    if (!posthog.has_opted_out_capturing()) {
      posthog.opt_out_capturing()
    }
    return true
  }
  return false
}

type AnalyticsCustomer = Pick<
  HttpTypes.StoreCustomer,
  "id" | "email" | "first_name" | "last_name"
> | null | undefined

type AnalyticsCart = {
  currency_code?: string | null
  total?: number | null
  item_subtotal?: number | null
  items?: { quantity?: number | null }[] | null
} | null | undefined

type EventContext = {
  customer?: AnalyticsCustomer
  cart?: AnalyticsCart
  pathname?: string | null
  sourceChannel?: string | null
}

type GlobalProperties = {
  session_id: string | null
  user_type: "guest" | "logged_in"
  cart_item_count: number
  cart_value: number
  page_type: "pdp" | "plp" | "cart" | "checkout" | "home"
  source_channel: string | null
  currency: string | null
  is_mobile: boolean
}

type RuntimeAnalyticsState = {
  customerId: string | null
  userType: "guest" | "logged_in"
  cartItemCount: number
  cartValue: number
  currency: string | null
  pathname: string | null
  sourceChannel: string | null
}

const runtimeState: RuntimeAnalyticsState = {
  customerId: null,
  userType: "guest",
  cartItemCount: 0,
  cartValue: 0,
  currency: null,
  pathname: null,
  sourceChannel: null,
}

function canUseBrowserApis() {
  return typeof window !== "undefined"
}

function getSessionId() {
  if (!canUseBrowserApis()) {
    return null
  }

  const existingSessionId = window.sessionStorage.getItem(SESSION_STORAGE_KEY)

  if (existingSessionId) {
    return existingSessionId
  }

  const generatedSessionId =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `session_${Date.now()}`

  window.sessionStorage.setItem(SESSION_STORAGE_KEY, generatedSessionId)
  return generatedSessionId
}

function getCartItemCount(cart?: AnalyticsCart) {
  const items = cart?.items ?? []

  return items.reduce((itemCount, item) => {
    return itemCount + (item.quantity ?? 0)
  }, 0)
}

function getCartValue(cart?: AnalyticsCart) {
  return cart?.total ?? cart?.item_subtotal ?? 0
}

function getCurrency(cart?: AnalyticsCart) {
  const nextCurrency = cart?.currency_code?.toUpperCase()
  return nextCurrency || runtimeState.currency || null
}

export function getPageType(pathname?: string | null): GlobalProperties["page_type"] {
  const normalizedPathname =
    pathname || runtimeState.pathname || (canUseBrowserApis() ? window.location.pathname : "")

  if (normalizedPathname.includes("/products/")) {
    return "pdp"
  }

  if (
    normalizedPathname.includes("/categories/") ||
    normalizedPathname.includes("/collections/") ||
    normalizedPathname.includes("/search")
  ) {
    return "plp"
  }

  if (normalizedPathname.includes("/cart")) {
    return "cart"
  }

  if (
    normalizedPathname.includes("/checkout") ||
    normalizedPathname.includes("/express-checkout") ||
    normalizedPathname.includes("/order/")
  ) {
    return "checkout"
  }

  return "home"
}

function getIsMobile() {
  if (!canUseBrowserApis()) {
    return false
  }

  return window.matchMedia("(max-width: 767px)").matches
}

export function persistSourceChannel(sourceChannel?: string | null) {
  if (!canUseBrowserApis()) {
    return null
  }

  const nextSourceChannel =
    sourceChannel ||
    new URLSearchParams(window.location.search).get("utm_source") ||
    window.localStorage.getItem(SOURCE_CHANNEL_STORAGE_KEY)

  if (!nextSourceChannel) {
    return null
  }

  window.localStorage.setItem(SOURCE_CHANNEL_STORAGE_KEY, nextSourceChannel)
  runtimeState.sourceChannel = nextSourceChannel

  return nextSourceChannel
}

function getSourceChannel(sourceChannel?: string | null) {
  return persistSourceChannel(sourceChannel)
}

function registerGlobalProperties(properties: GlobalProperties) {
  if (!canUseBrowserApis() || !posthog.__loaded) {
    return
  }

  posthog.register(properties)
}

export function syncAnalyticsContext(context: EventContext = {}) {
  const { cart, customer, pathname, sourceChannel } = context

  if (cart !== undefined) {
    runtimeState.cartItemCount = getCartItemCount(cart)
    runtimeState.cartValue = getCartValue(cart)
    runtimeState.currency = getCurrency(cart)
  }

  if (customer !== undefined) {
    runtimeState.customerId = customer?.id ?? null
    runtimeState.userType = customer?.id ? "logged_in" : "guest"
  }

  if (pathname !== undefined) {
    runtimeState.pathname = pathname
  }

  if (sourceChannel !== undefined) {
    runtimeState.sourceChannel = persistSourceChannel(sourceChannel)
  } else {
    runtimeState.sourceChannel = getSourceChannel()
  }

  registerGlobalProperties(buildGlobalProperties({}, context))
}

export function buildGlobalProperties(
  properties: Record<string, unknown> = {},
  context: EventContext = {}
) {
  const customer = context.customer
  const cart = context.cart

  const globalProperties: GlobalProperties = {
    session_id: getSessionId(),
    user_type: customer?.id ? "logged_in" : runtimeState.userType,
    cart_item_count:
      cart !== undefined ? getCartItemCount(cart) : runtimeState.cartItemCount,
    cart_value: cart !== undefined ? getCartValue(cart) : runtimeState.cartValue,
    page_type: getPageType(context.pathname),
    source_channel: getSourceChannel(context.sourceChannel),
    currency: getCurrency(cart),
    is_mobile: getIsMobile(),
  }

  return {
    ...globalProperties,
    ...properties,
  }
}

export function captureAnalyticsEvent(
  eventName: string,
  properties: Record<string, unknown> = {},
  context: EventContext = {}
) {
  if (!canUseBrowserApis() || !posthog.__loaded) {
    return
  }

  syncAnalyticsContext(context)
  posthog.capture(eventName, buildGlobalProperties(properties, context))
}

/** Matches `SubscribeSource` on the server; duplicated here to avoid importing a "use server" module in client code. */
export type NewsletterSubscriptionSource =
  | "popup"
  | "footer"
  | "home"
  | "checkout"
  | "register"

/** Fired when the store API confirms a newsletter signup (do not include raw email). */
export function captureNewsletterSubscribed(
  subscriptionSource: NewsletterSubscriptionSource,
  formName: string,
  context: EventContext = {}
) {
  captureAnalyticsEvent(
    "newsletter_subscribed",
    {
      subscription_source: subscriptionSource,
      form_name: formName,
    },
    context
  )
}

export function capturePageView(pathname?: string | null) {
  if (!canUseBrowserApis() || !posthog.__loaded) {
    return
  }

  const currentUrl = window.location.href

  syncAnalyticsContext({ pathname })

  posthog.capture(
    "$pageview",
    buildGlobalProperties(
      {
        $current_url: currentUrl,
      },
      { pathname }
    )
  )
}

export function syncPostHogIdentity(customer?: AnalyticsCustomer) {
  if (!canUseBrowserApis() || !posthog.__loaded) {
    return
  }

  // Internal/test accounts: suppress all capture and never identify them.
  if (
    applyInternalEmailOptOut(customer?.email, {
      firstName: customer?.first_name,
      lastName: customer?.last_name,
    })
  ) {
    return
  }

  const previousCustomerId = window.localStorage.getItem(IDENTIFIED_USER_STORAGE_KEY)
  const nextCustomerId = customer?.id ?? null

  if (nextCustomerId) {
    if (previousCustomerId !== nextCustomerId) {
      posthog.identify(
        nextCustomerId,
        customer?.email || customer?.first_name || customer?.last_name
          ? {
              email: customer?.email,
              first_name: customer?.first_name,
              last_name: customer?.last_name,
            }
          : {}
      )
      window.localStorage.setItem(IDENTIFIED_USER_STORAGE_KEY, nextCustomerId)
    }

    syncAnalyticsContext({ customer })
    return
  }

  if (previousCustomerId) {
    posthog.reset()
    window.localStorage.removeItem(IDENTIFIED_USER_STORAGE_KEY)
  }

  syncAnalyticsContext({ customer: null })
}
